import { useCallback, useEffect, useState } from "react";
import { z } from "zod";

import { GET_ALL_EMPLOYEES_NAME } from "../../../../../config/Endpoints.js";
import type { SelectOption } from "../../../ui/select/select.tsx";
import { ApiError, getApi } from "../../../../utils/api.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { unwrapApiDataArray } from "../utils/unwrap-api-data.ts";

const employeeRowSchema = z.object({
  id: z.union([z.number(), z.string()]).transform((v) => Number(v)),
  empName: z.string(),
  employeeType: z.string().nullish(),
});

function toEmployeeOptions(items: unknown[]): SelectOption[] {
  const parsed = z.array(employeeRowSchema).safeParse(items);
  if (!parsed.success) return [];
  return parsed.data.map((row) => {
    const type = row.employeeType?.trim();
    return {
      value: String(row.id),
      label: type ? `${row.empName} (${type})` : row.empName,
    };
  });
}

export function useEmployeesLookup() {
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchEmployees = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await getApi<unknown>(
        GET_ALL_EMPLOYEES_NAME,
        accessToken(),
        "json",
        signal,
      );
      setEmployees(toEmployeeOptions(unwrapApiDataArray(data)));
    } catch (e) {
      const msg =
        e instanceof ApiError ? e.message : "Failed to load employees.";
      setError(msg);
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const ac = new AbortController();
    void fetchEmployees(ac.signal);
    return () => ac.abort();
  }, [fetchEmployees]);

  return {
    employees,
    loading,
    error,
    refetch: fetchEmployees,
  };
}
